import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

type ToastType =
  | 'credit'
  | 'welcome'
  | 'firstShare'
  | 'tripSaved'    // note: trip-saved toast is in TripsContext, this is the *credit* toast
  | 'expired';

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------
interface UiFeedbackContextValue {
  // ── Toasts ──
  creditToast: string | null;
  /** How the credit toast renders: teal for good news, red for a failure. */
  creditToastSeverity: 'success' | 'error';
  /** Optional button inside the toast, e.g. "Sign in". */
  creditToastAction: { label: string; onClick: () => void } | null;
  welcomeToast: boolean;
  shareToast: string | null;
  expiredToast: boolean;

  // ── Banners ──
  referralBanner: boolean;
  setReferralBanner: (v: boolean) => void;

  // ── Modals ──
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;
  authOpen: boolean;
  setAuthOpen: (v: boolean) => void;

  // ── Unified toast API ──
  showToast: (type: ToastType, message?: string) => void;
  dismissToast: (type: ToastType) => void;
  /** Show a credit toast with auto-dismiss */
  showCreditToast: (message: string, durationMs?: number, action?: { label: string; onClick: () => void }) => void;
  /** A failure the user must see. Same slot, red, longer default. */
  showErrorToast: (message: string, durationMs?: number) => void;
}

const UiFeedbackContext = createContext<UiFeedbackContextValue | null>(null);

export function useUiFeedbackContext() {
  const ctx = useContext(UiFeedbackContext);
  if (!ctx) throw new Error('useUiFeedbackContext must be used within <UiFeedbackProvider>');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
interface UiFeedbackProviderProps {
  children: ReactNode;
  initialReferralBanner?: boolean;
}

export function UiFeedbackProvider({
  children,
  initialReferralBanner = false,
}: UiFeedbackProviderProps) {
  // ── Toasts ──
  const [creditToast, setCreditToast] = useState<string | null>(null);
  const [creditToastSeverity, setCreditToastSeverity] = useState<'success' | 'error'>('success');
  const [creditToastAction, setCreditToastAction] = useState<{ label: string; onClick: () => void } | null>(null);
  const [welcomeToast, setWelcomeToast] = useState(false);
  const [shareToast, setShareToast] = useState<string | null>(null);
  const [expiredToast, setExpiredToast] = useState(false);

  // ── Banners ──
  const [referralBanner, setReferralBanner] = useState(initialReferralBanner);

  // ── Modals ──
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  // ── Unified API ──
  const showToast = useCallback((type: ToastType, message?: string) => {
    switch (type) {
      case 'credit':
        setCreditToastSeverity('success');
        setCreditToast(message ?? null);
        break;
      case 'welcome':
        setWelcomeToast(true);
        setTimeout(() => setWelcomeToast(false), 4000);
        break;
      case 'firstShare':
        setShareToast('first');
        setTimeout(() => setShareToast(null), 3000);
        break;
      case 'expired':
        setExpiredToast(true);
        break;
    }
  }, []);

  const dismissToast = useCallback((type: ToastType) => {
    switch (type) {
      case 'credit':
        setCreditToast(null);
        break;
      case 'welcome':
        setWelcomeToast(false);
        break;
      case 'firstShare':
        setShareToast(null);
        break;
      case 'expired':
        setExpiredToast(false);
        break;
    }
  }, []);

  const showCreditToast = useCallback((message: string, durationMs: number = 4000, action?: { label: string; onClick: () => void }) => {
    setCreditToastSeverity('success');
    setCreditToastAction(action ?? null);
    setCreditToast(message);
    setTimeout(() => setCreditToast(null), durationMs);
  }, []);

  const showErrorToast = useCallback((message: string, durationMs: number = 6000) => {
    setCreditToastSeverity('error');
    setCreditToastAction(null);
    setCreditToast(message);
    setTimeout(() => setCreditToast(null), durationMs);
  }, []);

  const value: UiFeedbackContextValue = {
    creditToast,
    creditToastSeverity,
    creditToastAction,
    welcomeToast,
    shareToast,
    expiredToast,
    referralBanner,
    setReferralBanner,
    settingsOpen,
    setSettingsOpen,
    authOpen,
    setAuthOpen,
    showToast,
    dismissToast,
    showCreditToast,
    showErrorToast,
  };

  return <UiFeedbackContext.Provider value={value}>{children}</UiFeedbackContext.Provider>;
}
